import { useEffect, useRef, useState, type HTMLAttributes } from "react";
import { copyText } from "./clipboard";
import { cx } from "./cx";
import { middleTruncate } from "./format";
import { IconCheck, IconCopy } from "./icons";
import { VisuallyHidden } from "./VisuallyHidden";
import "./Mono.css";

export interface MonoProps extends Omit<HTMLAttributes<HTMLElement>, "children"> {
  /** Discord snowflake, stream id, hash… Always copied in full. */
  value: string | number;
  /** Click to copy. Default true. */
  copy?: boolean;
  /** Visually shorten to "1234…7890" (full value stays in title and copy). */
  truncate?: boolean;
  /** What is being copied, for the accessible name ("Copy channel ID"). Default "ID". */
  kind?: string;
  /** Fires after a copy attempt. */
  onCopied?: (value: string, ok: boolean) => void;
}

/** Monospace value; click (or Enter/Space) copies it and confirms with "Copied". */
export function Mono({
  value,
  copy = true,
  truncate = false,
  kind = "ID",
  onCopied,
  className,
  ...rest
}: MonoProps) {
  const text = String(value);
  const shown = truncate ? middleTruncate(text) : text;
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  if (!copy) {
    return (
      <code className={cx("ui-mono", className)} title={truncate ? text : undefined} {...rest}>
        {shown}
      </code>
    );
  }

  const handleCopy = async () => {
    const ok = await copyText(text);
    setState(ok ? "copied" : "failed");
    onCopied?.(text, ok);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), 1600);
  };

  return (
    <button
      type="button"
      className={cx("ui-mono", "ui-mono--copy", state === "copied" && "ui-mono--copied", className)}
      title={`${text} — click to copy`}
      aria-label={`Copy ${kind} ${text}`}
      onClick={handleCopy}
      {...rest}
    >
      <code className="ui-mono__text">{shown}</code>
      <span className="ui-mono__icon" aria-hidden="true">
        {state === "copied" ? <IconCheck /> : <IconCopy />}
      </span>
      <VisuallyHidden aria-live="polite">
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : ""}
      </VisuallyHidden>
    </button>
  );
}
